import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { previewAlerts, sendAlerts, sendTestEmail, formatCurrency } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Mail, Send, AlertTriangle, Clock, CreditCard, FileText, RefreshCw, CheckCircle, Eye } from "lucide-react";

export default function Alerts() {
  const { user, canEdit } = useAuth();
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [daysAhead, setDaysAhead] = useState("7");
  const [sendTo, setSendTo] = useState("admin_and_full");

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await previewAlerts(parseInt(daysAhead));
      setAlerts(res.data);
    } catch {
      toast.error("Failed to load alerts");
    } finally {
      setLoading(false);
    }
  }, [daysAhead]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const handleSendAlerts = async () => {
    setSending(true);
    try {
      const res = await sendAlerts({ send_to: sendTo, days_ahead: parseInt(daysAhead) });
      toast.success(`Alerts sent to ${res.data.sent} recipient(s)`);
      if (res.data.failed?.length > 0) {
        toast.error(`Failed for: ${res.data.failed.join(", ")}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send alerts");
    } finally {
      setSending(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) { toast.error("Enter an email address"); return; }
    setSendingTest(true);
    try {
      await sendTestEmail(testEmail);
      toast.success(`Test email sent to ${testEmail}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  const isAdmin = user?.role === "admin";
  const data = alerts?.alerts || {};

  return (
    <div className="space-y-6 animate-fade-in" data-testid="alerts-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Email Alerts</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Review and send financial alert emails
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={daysAhead} onValueChange={setDaysAhead}>
            <SelectTrigger className="w-28 sm:w-32" data-testid="days-ahead-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">3 days</SelectItem>
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="14">14 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAlerts} data-testid="refresh-alerts">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-brand-orange border-t-transparent rounded-full" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Card className={`border-l-4 ${(data.overdue_invoices?.length || 0) > 0 ? 'border-l-red-500' : 'border-l-emerald-500'}`}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-4 h-4 text-red-500" />
                  <span className="text-xs font-medium text-slate-500 uppercase">Overdue Invoices</span>
                </div>
                <p className="text-xl sm:text-2xl font-bold text-slate-900" data-testid="overdue-count">
                  {data.overdue_invoices?.length || 0}
                </p>
              </CardContent>
            </Card>
            <Card className={`border-l-4 ${(data.upcoming_payables?.length || 0) > 0 ? 'border-l-amber-500' : 'border-l-emerald-500'}`}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-amber-500" />
                  <span className="text-xs font-medium text-slate-500 uppercase">Due Payables</span>
                </div>
                <p className="text-xl sm:text-2xl font-bold text-slate-900" data-testid="payables-count">
                  {data.upcoming_payables?.length || 0}
                </p>
              </CardContent>
            </Card>
            <Card className={`border-l-4 ${(data.upcoming_credit_cards?.length || 0) > 0 ? 'border-l-purple-500' : 'border-l-emerald-500'}`}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard className="w-4 h-4 text-purple-500" />
                  <span className="text-xs font-medium text-slate-500 uppercase">Card Dues</span>
                </div>
                <p className="text-xl sm:text-2xl font-bold text-slate-900" data-testid="cards-count">
                  {data.upcoming_credit_cards?.length || 0}
                </p>
              </CardContent>
            </Card>
            <Card className={`border-l-4 ${(data.statutory_dues?.length || 0) > 0 ? 'border-l-violet-500' : 'border-l-emerald-500'}`}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4 text-violet-500" />
                  <span className="text-xs font-medium text-slate-500 uppercase">Statutory</span>
                </div>
                <p className="text-xl sm:text-2xl font-bold text-slate-900" data-testid="statutory-count">
                  {data.statutory_dues?.length || 0}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Alert Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Overdue Invoices */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <FileText className="w-4 h-4 text-red-500" />
                  Overdue Invoices
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {(data.overdue_invoices?.length || 0) === 0 ? (
                  <div className="flex items-center gap-2 py-4 text-sm text-emerald-600">
                    <CheckCircle className="w-4 h-4" /> No overdue invoices
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {data.overdue_invoices.map((inv, i) => (
                      <div key={`inv-${inv.invoice_number}-${i}`} className="flex items-center justify-between p-2 bg-red-50 rounded-lg text-sm">
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 truncate">{inv.client_name}</p>
                          <p className="text-xs text-slate-500">{inv.company} &middot; {inv.invoice_number}</p>
                        </div>
                        <div className="text-right flex-shrink-0 ml-2">
                          <p className="font-bold text-red-600 tabular-nums">{formatCurrency(inv.balance)}</p>
                          <p className="text-xs text-red-500">{inv.days_overdue}d overdue</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Upcoming Payables */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-500" />
                  Payables Due
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {(data.upcoming_payables?.length || 0) === 0 ? (
                  <div className="flex items-center gap-2 py-4 text-sm text-emerald-600">
                    <CheckCircle className="w-4 h-4" /> No payables due
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {data.upcoming_payables.map((p, i) => (
                      <div key={`pay-${p.vendor_name}-${i}`} className={`flex items-center justify-between p-2 rounded-lg text-sm ${p.days_until_due < 0 ? 'bg-red-50' : 'bg-amber-50'}`}>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 truncate">{p.vendor_name}</p>
                          <p className="text-xs text-slate-500">{p.company} &middot; Due {p.due_date}</p>
                        </div>
                        <div className="text-right flex-shrink-0 ml-2">
                          <p className="font-bold text-slate-900 tabular-nums">{formatCurrency(p.balance)}</p>
                          <p className={`text-xs ${p.days_until_due < 0 ? 'text-red-500' : 'text-amber-600'}`}>
                            {p.days_until_due < 0 ? `${Math.abs(p.days_until_due)}d overdue` : `${p.days_until_due}d left`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Credit Cards */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-purple-500" />
                  Credit Card Dues
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {(data.upcoming_credit_cards?.length || 0) === 0 ? (
                  <div className="flex items-center gap-2 py-4 text-sm text-emerald-600">
                    <CheckCircle className="w-4 h-4" /> No card dues
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {data.upcoming_credit_cards.map((cc, i) => (
                      <div key={`cc-${cc.card_name}-${i}`} className="flex items-center justify-between p-2 bg-purple-50 rounded-lg text-sm">
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 truncate">{cc.card_name}</p>
                          <p className="text-xs text-slate-500">{cc.bank_name} &middot; Due {cc.due_date}</p>
                        </div>
                        <p className="font-bold text-purple-700 tabular-nums flex-shrink-0 ml-2">{formatCurrency(cc.current_balance)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Statutory */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-violet-500" />
                  Statutory Dues
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {(data.statutory_dues?.length || 0) === 0 ? (
                  <div className="flex items-center gap-2 py-4 text-sm text-emerald-600">
                    <CheckCircle className="w-4 h-4" /> No statutory dues pending
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {data.statutory_dues.map((s, i) => (
                      <div key={`stat-${s.name}-${i}`} className="flex items-center justify-between p-2 bg-violet-50 rounded-lg text-sm">
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 truncate">{s.name}</p>
                          <p className="text-xs text-slate-500">{s.company} &middot; {s.due_date}</p>
                        </div>
                        <p className="font-bold text-violet-700 tabular-nums flex-shrink-0 ml-2">{formatCurrency(s.amount)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Send Actions — Admin Only */}
          {isAdmin && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Send Alert Emails */}
              <Card className="border-brand-navy/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Send className="w-4 h-4 text-brand-navy" />
                    Send Alert Emails
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Send to</Label>
                    <Select value={sendTo} onValueChange={setSendTo}>
                      <SelectTrigger data-testid="send-to-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin_only">Admin only</SelectItem>
                        <SelectItem value="admin_and_full">Admin + Full Access users</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleSendAlerts}
                    disabled={sending}
                    className="w-full bg-brand-navy hover:bg-brand-navy/90 text-white"
                    data-testid="send-alerts-btn"
                  >
                    {sending ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                    ) : (
                      <><Mail className="w-4 h-4 mr-2" /> Send Alert Report</>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Test Email */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Eye className="w-4 h-4 text-slate-500" />
                    Send Test Email
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Recipient email</Label>
                    <Input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="you@example.com"
                      data-testid="test-email-input"
                    />
                  </div>
                  <Button
                    onClick={handleTestEmail}
                    disabled={sendingTest}
                    variant="outline"
                    className="w-full"
                    data-testid="send-test-btn"
                  >
                    {sendingTest ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Send Test</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}
